/**
 * UTM Modal Custom Element
 *
 * Displays a modal when a matching UTM parameter is detected in the URL.
 * Extends ModalDialog to add UTM parameter detection and auto-display.
 * Shows modal only once per session when matching UTM parameters are detected.
 *
 * Note: Requires ModalDialog from global.js. Waits for it to be defined before registering.
**/

/* global ModalDialog */
customElements.whenDefined('modal-dialog').then(() => {
  class UTMModal extends ModalDialog {
    constructor() {
      super();
      this.sessionKey = `utm_modal_shown_${this.id}`;

      // Override the click-outside-to-close behavior since we're utm-modal, not modal-dialog
      // The parent class checks for nodeName === 'MODAL-DIALOG', which doesn't match 'UTM-MODAL'
      if (!this.classList.contains('media-modal')) {
        this.addEventListener('click', (event) => {
          if (event.target.nodeName === 'UTM-MODAL') this.hide();
        });
      }
    }

    connectedCallback() {
      // Call parent connectedCallback first
      super.connectedCallback();

      // Initialize UTM-specific logic
      this.initUTM();
    }

    initUTM() {
      // Check if already shown this session
      if (sessionStorage.getItem(this.sessionKey)) {
        this.dispatchPriorityResolved('already-shown');
        return; // Already shown, don't show again
      }

      // Set up CTA close button if it exists (button with no URL)
      const ctaCloseButton = this.querySelector('[id^="ModalCloseCTA-"]');
      if (ctaCloseButton) {
        ctaCloseButton.addEventListener('click', () => {
          this.hide();
        });
      }

      // Check if URL has matching UTM parameter and show if match found
      if (this.hasMatchingUTMParameter()) {
        this.triggerModal();
      } else {
        // No matching UTM parameters - dispatch event so other modals can proceed
        this.dispatchPriorityResolved('no-match');
      }
    }

    /**
     * Parse URL parameters into an object
     * @returns {Object} Key-value pairs of URL parameters
     */
    getURLParameters() {
      const params = {};
      const searchParams = new URLSearchParams(window.location.search);

      for (const [key, value] of searchParams.entries()) {
        params[key] = value.toLowerCase();
      }

      return params;
    }

    /**
     * Check if URL contains a matching UTM parameter
     * Dynamically checks all data-utm-* attributes on the modal element
     * @returns {boolean} True if there's a match
     */
    hasMatchingUTMParameter() {
      const urlParams = this.getURLParameters();
      const dataset = this.dataset;

      for (const key in dataset) {
        // Check if this is a UTM parameter (e.g., utmSource, utmMedium, utmCampaign)
        if (key.startsWith('utm')) {
          // Convert camelCase back to snake_case (utmSource -> utm_source)
          const paramName = key.replace(/([A-Z])/g, '_$1').toLowerCase();
          const configuredValues = dataset[key];

          if (!configuredValues) continue;

          // Get the URL parameter value
          const urlParamValue = urlParams[paramName];
          if (!urlParamValue) continue;

          // Split configured values and check for match
          const valuesToMatch = configuredValues
            .split(',')
            .map(val => val.trim().toLowerCase())
            .filter(val => val.length > 0);

          if (valuesToMatch.includes(urlParamValue)) {
            return true; // Found a match!
          }
        }
      }

      return false; // No matches found
    }

    /**
     * Trigger the modal display and mark as shown
     */
    triggerModal() {
      try {
        // Call parent's show() method
        this.show();
        sessionStorage.setItem(this.sessionKey, 'true');

      } catch (error) {
        console.error('UTM Modal: Failed to show modal', error);
      }
    }

    /**
     * Override hide() to dispatch custom event when modal closes
     */
    hide() {
      super.hide();

      // Dispatch custom event that other popups can listen for
      this.dispatchPriorityResolved('closed');
    }

    /**
     * Dispatch priority-modal-resolved event
     * @param {string} reason - Reason for resolution ('closed', 'no-match', 'already-shown')
     */
    dispatchPriorityResolved(reason) {
      const event = new CustomEvent('priority-modal-resolved', {
        bubbles: true,
        detail: { modalId: this.id, reason }
      });
      document.dispatchEvent(event);
    }
  }

  // Define the custom element after ModalDialog is available
  if (!customElements.get('utm-modal')) customElements.define('utm-modal', UTMModal);
});

